import { axiosClient } from "@/lib/axios";
import { HttpError } from "@/types/error";
import { PublicSession, WithPagination } from "@classview/shared-types";
import { UseQueryOptions, useQuery } from "@tanstack/react-query";

import { GetSessionByOrgArgs } from "../types/get-session-args";

const getSessionsByOrg = async ({
  organisationId,
  pageSize = 10,
  pageIndex = 0,
  search,
  futureOnly = false,
  day,
}: {
  organisationId?: string;
  pageSize?: number;
  pageIndex?: number;
  search?: string;
  futureOnly?: boolean;
  day?: string;
}): Promise<WithPagination<PublicSession>> => {
  const offset = pageIndex * pageSize;
  const searchParams = new URLSearchParams({
    limit: pageSize.toString(),
    offset: offset.toString(),
    futureOnly: futureOnly ? "true" : "false",
  });

  if (search) {
    searchParams.append("search", search);
  }

  if (day) {
    searchParams.append("day", day);
  }

  if (organisationId) {
    searchParams.append("orgId", organisationId);
  }

  const response = await axiosClient.get(
    `/sessions?${searchParams.toString()}`,
  );
  return response.data;
};

export function getSessionsByOrgQueryKey({
  organisationId,
  pageSize,
  pageIndex,
  search,
  futureOnly,
  day,
}: GetSessionByOrgArgs) {
  return [
    "sessions",
    organisationId ?? "all",
    `pageindex-${pageIndex}`,
    `pagesize-${pageSize}`,
    `search-${search || "no-search"}`,
    futureOnly ? "future-only" : "all",
    day,
  ];
}

export function useGetSessionsByOrg(
  {
    pageSize,
    pageIndex,
    search,
    futureOnly = false,
    organisationId,
    day,
  }: GetSessionByOrgArgs,
  queryOptions?: Partial<
    UseQueryOptions<WithPagination<PublicSession>, HttpError>
  >,
) {
  return useQuery({
    queryKey: getSessionsByOrgQueryKey({
      organisationId,
      pageIndex,
      pageSize,
      search,
      futureOnly,
      day,
    }),
    queryFn: () =>
      getSessionsByOrg({
        organisationId: organisationId,
        pageSize,
        pageIndex,
        search: search ?? undefined,
        futureOnly,
        day,
      }),
    ...queryOptions,
  });
}
